import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../store/indexRedux';
import { autoSelectCompany } from '../utils/autoSelectCompany';
import Loading from '../components/ui/Loading';
import ContractDetails from '../components/activeServices/ContractDetails';
import {
  fetchContractPermalink,
  PermalinkError,
  ContractPermalinkResponse,
  ContractPermalinkType,
} from '../api/permalinks';

const VALID_TYPES: ContractPermalinkType[] = ['injection', 'offtake', 'flex'];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type LoadStatus = 'loading' | 'ok' | 'notfound' | 'unauthorized' | 'error';

const ContractPermalinkPage: React.FC = () => {
  const { t } = useTranslation('permalinks');
  const { type, id } = useParams<{ type: string; id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const selectedUserCompany = useSelector((s: RootState) => s.selectedCompany.selectedUserCompany);

  const [data, setData] = useState<ContractPermalinkResponse | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');

  const isValidType = !!type && VALID_TYPES.includes(type as ContractPermalinkType);

  useEffect(() => {
    if (!isValidType || !id) {
      setStatus('notfound');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    fetchContractPermalink(type as ContractPermalinkType, id)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setStatus('ok');

        // Auto-select the owning company (member path). Best-effort: an admin not
        // linked to the company gets a 404 from getUserCompany — keep the prior
        // selection, the page stays self-contained (spec §6.4).
        autoSelectCompany(dispatch, resp.company.id, selectedUserCompany?.company_id);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof PermalinkError && err.status === 401) setStatus('unauthorized');
        else if (err instanceof PermalinkError && err.status === 404) setStatus('notfound');
        else setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // Re-fetch only when the target contract changes; selectedUserCompany is read for a
    // one-off comparison and must not retrigger the fetch (would loop after auto-select).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  if (status === 'loading') return <Loading />;

  if (status !== 'ok' || !data) {
    const message =
      status === 'unauthorized' ? t('sessionExpired') : status === 'error' ? t('errorGeneric') : t('contractNotFound');
    return (
      <div className='flex flex-col items-center mt-32 px-4'>
        <div className='lg:w-[560px] w-full p-6 border border-primary-100 dark:border-primary-800 rounded-lg bg-white dark:bg-primary-900 text-center'>
          <p className='text-primary-600 dark:text-primary-100 mb-6'>{message}</p>
          {status === 'unauthorized' ? (
            <NavLink
              to='/'
              className='inline-block px-5 py-2.5 bg-primary-600 text-white text-sm rounded-3xl hover:bg-primary-700 font-semibold'
            >
              {t('logInAgain')}
            </NavLink>
          ) : (
            <NavLink
              to='/overview'
              className='inline-block px-5 py-2.5 bg-primary-600 text-white text-sm rounded-3xl hover:bg-primary-700 font-semibold'
            >
              {t('backToOverview')}
            </NavLink>
          )}
        </div>
      </div>
    );
  }

  const contractType = data.type;

  return (
    <div className='md:mx-16 pb-[80px] mt-20 mb-16'>
      <h2 className='text-primary-600 dark:text-white lg:text-[1.875rem] font-semibold mb-2'>{t('contractTitle')}</h2>
      <p className='text-primary-400 text-sm font-semibold mb-8'>
        {data.company.name}
        {data.company.kbo ? ` · ${t('kbo')} ${data.company.kbo}` : ''}
      </p>

      <div className='mb-8'>
        <NavLink
          to='/overview'
          className='px-[1.25rem] py-[0.53rem] bg-primary-600 dark:bg-primary-600 text-sm text-white rounded-3xl hover:bg-primary-700 dark:hover:bg-primary-700 font-semibold'
        >
          {t('backToOverview')}
        </NavLink>
      </div>

      {!data.is_latest && data.superseded_by != null && (
        <div className='flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 mb-6 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'>
          <span className='text-sm font-medium text-amber-800 dark:text-amber-300'>
            {t('superseded')}
          </span>
          <NavLink
            to={`/contract/${contractType}/${data.superseded_by}`}
            className='text-sm font-semibold text-amber-900 dark:text-amber-200 underline'
          >
            {t('viewCurrent', { id: data.superseded_by })}
          </NavLink>
        </div>
      )}

      <div className='bg-white dark:bg-primary-900 rounded-xl border border-primary-100 dark:border-primary-800 px-6 py-5'>
        <ContractDetails
          service={data.contract}
          direction={contractType === 'flex' ? undefined : (capitalize(contractType) as 'Injection' | 'Offtake')}
        />
      </div>
    </div>
  );
};

export default ContractPermalinkPage;
